import {
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useEffect,
  useId,
} from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, Check, ChevronDown, Inbox, LoaderCircle, Search, X } from 'lucide-react'
import { toast } from 'sonner'
import type { WeekStatus } from '@/types'
import { cn } from '@/utils'

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'soft'
type ButtonSize = 'sm' | 'md' | 'lg' | 'icon'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  leftIcon?: ReactNode
}

const buttonVariants: Record<ButtonVariant, string> = {
  primary: 'bg-forest-700 text-white shadow-sm hover:bg-forest-800 focus-visible:ring-forest-500 disabled:bg-forest-300',
  secondary: 'bg-saffron-400 text-forest-950 shadow-sm hover:bg-saffron-300 focus-visible:ring-saffron-400',
  outline: 'border border-slate-200 bg-white text-slate-700 hover:border-forest-300 hover:bg-forest-50 focus-visible:ring-forest-400',
  ghost: 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus-visible:ring-slate-300',
  danger: 'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-400 disabled:bg-red-300',
  soft: 'bg-forest-50 text-forest-800 hover:bg-forest-100 focus-visible:ring-forest-300',
}

const buttonSizes: Record<ButtonSize, string> = {
  sm: 'min-h-9 px-3 text-sm',
  md: 'min-h-11 px-4 text-sm',
  lg: 'min-h-12 px-5 text-base',
  icon: 'h-10 w-10 p-0',
}

export function Button({
  className,
  variant = 'primary',
  size = 'md',
  loading,
  disabled,
  leftIcon,
  children,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-65 active:scale-[.98]',
        buttonVariants[variant],
        buttonSizes[size],
        className,
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" /> : leftIcon}
      {children}
    </button>
  )
}

export function IconButton({ 'aria-label': ariaLabel, ...props }: ButtonProps) {
  return <Button size="icon" variant="ghost" aria-label={ariaLabel} {...props} />
}

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('rounded-2xl border border-slate-200/80 bg-white shadow-card', className)} {...props} />
}

export function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-5 pb-3 pt-5 sm:px-6 sm:pt-6', className)} {...props} />
}

export function CardContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-5 pb-5 sm:px-6 sm:pb-6', className)} {...props} />
}

export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn('font-display text-lg font-bold tracking-tight text-slate-900', className)} {...props} />
}

type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'purple'
const badgeTones: Record<BadgeTone, string> = {
  neutral: 'bg-slate-100 text-slate-700 ring-slate-200',
  success: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  warning: 'bg-amber-50 text-amber-800 ring-amber-200',
  danger: 'bg-red-50 text-red-700 ring-red-200',
  info: 'bg-blue-50 text-blue-700 ring-blue-200',
  purple: 'bg-violet-50 text-violet-700 ring-violet-200',
}

export function Badge({
  className,
  tone = 'neutral',
  ...props
}: HTMLAttributes<HTMLSpanElement> & { tone?: BadgeTone }) {
  return (
    <span
      className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ring-1 ring-inset', badgeTones[tone], className)}
      {...props}
    />
  )
}

const statusLabels: Record<WeekStatus, string> = {
  draft: 'Borrador',
  open: 'Abierta',
  closed: 'Cerrada',
  finished: 'Finalizada',
}

export function StatusBadge({ status, className }: { status: WeekStatus; className?: string }) {
  const tone: BadgeTone = status === 'open' ? 'success' : status === 'draft' ? 'neutral' : status === 'closed' ? 'warning' : 'purple'
  return <Badge tone={tone} className={className}><span className="h-1.5 w-1.5 rounded-full bg-current" />{statusLabels[status]}</Badge>
}

interface FieldProps {
  label: string
  htmlFor?: string
  hint?: string
  error?: string
  required?: boolean
  children: ReactNode
  className?: string
}

export function Field({ label, htmlFor, hint, error, required, children, className }: FieldProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <label htmlFor={htmlFor} className="block text-sm font-semibold text-slate-700">
        {label}{required && <span className="ml-1 text-red-500" aria-hidden="true">*</span>}
      </label>
      {children}
      {error ? <p className="text-sm text-red-600" role="alert">{error}</p> : hint ? <p className="text-xs text-slate-500">{hint}</p> : null}
    </div>
  )
}

const controlClass = 'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[15px] text-slate-900 outline-none transition placeholder:text-slate-400 hover:border-slate-300 focus:border-forest-500 focus:ring-4 focus:ring-forest-500/10 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500'

export const Input = ({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) => (
  <input className={cn(controlClass, className)} {...props} />
)

export const Textarea = ({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea className={cn(controlClass, 'min-h-24 resize-y', className)} {...props} />
)

export function Select({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className="relative">
      <select className={cn(controlClass, 'appearance-none pr-10', className)} {...props}>{children}</select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
    </div>
  )
}

export function SearchInput({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className={cn('relative', className)}>
      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <Input className="pl-10" type="search" {...props} />
    </div>
  )
}

export function Checkbox({ label, className, ...props }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  const generatedId = useId()
  const id = props.id ?? generatedId
  return (
    <label htmlFor={id} className={cn('flex cursor-pointer items-start gap-3 text-sm text-slate-700', className)}>
      <input id={id} type="checkbox" className="mt-0.5 h-4 w-4 rounded border-slate-300 text-forest-700 focus:ring-forest-500" {...props} />
      <span>{label}</span>
    </label>
  )
}

export function Progress({ value, label, className }: { value: number; label?: string; className?: string }) {
  const safeValue = Math.min(100, Math.max(0, value))
  return (
    <div className={className}>
      {label && <div className="mb-2 flex justify-between text-sm font-medium text-slate-600"><span>{label}</span><span>{Math.round(safeValue)}%</span></div>}
      <div className="h-2.5 overflow-hidden rounded-full bg-slate-100" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={safeValue}>
        <div className="h-full rounded-full bg-forest-600 transition-all duration-500" style={{ width: `${safeValue}%` }} />
      </div>
    </div>
  )
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('relative overflow-hidden rounded-xl bg-slate-100 before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_1.6s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/70 before:to-transparent', className)} aria-hidden="true" />
}

export function PageSkeleton() {
  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8" aria-label="Cargando contenido">
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-28 w-full" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{Array.from({ length: 4 }, (_, index) => <Skeleton key={index} className="h-32" />)}</div>
      <Skeleton className="h-80 w-full" />
    </div>
  )
}

export function EmptyState({
  title,
  description,
  action,
  icon,
  className,
}: {
  title: string
  description?: string
  action?: ReactNode
  icon?: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center', className)}>
      <div className="mb-4 grid h-12 w-12 place-items-center rounded-2xl bg-forest-50 text-forest-700">{icon ?? <Inbox className="h-6 w-6" />}</div>
      <h3 className="font-display text-lg font-bold text-slate-900">{title}</h3>
      {description && <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  )
}

export function Avatar({ name, src, className }: { name: string; src?: string | null; className?: string }) {
  const letters = name.split(' ').filter(Boolean).slice(0, 2).map((part) => part.charAt(0)).join('').toUpperCase()
  return src ? (
    <img src={src} alt="" className={cn('h-10 w-10 rounded-full object-cover ring-2 ring-white', className)} />
  ) : (
    <span className={cn('grid h-10 w-10 shrink-0 place-items-center rounded-full bg-forest-100 text-sm font-bold text-forest-800 ring-2 ring-white', className)} aria-hidden="true">{letters}</span>
  )
}

interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
}

export function Modal({ open, onClose, title, description, children, footer, size = 'md' }: ModalProps) {
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const listener = (event: KeyboardEvent) => event.key === 'Escape' && onClose()
    window.addEventListener('keydown', listener)
    return () => {
      document.body.style.overflow = previous
      window.removeEventListener('keydown', listener)
    }
  }, [open, onClose])

  if (!open) return null
  const widths = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl', xl: 'max-w-5xl' }
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-950/45 p-0 backdrop-blur-[2px] sm:items-center sm:p-5" role="dialog" aria-modal="true" aria-labelledby="modal-title" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className={cn('max-h-[94dvh] w-full overflow-hidden rounded-t-3xl bg-white shadow-2xl animate-fade-up sm:rounded-2xl', widths[size])}>
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4 sm:px-6">
          <div><h2 id="modal-title" className="font-display text-xl font-bold text-slate-900">{title}</h2>{description && <p className="mt-1 text-sm text-slate-500">{description}</p>}</div>
          <IconButton onClick={onClose} aria-label="Cerrar"><X className="h-5 w-5" /></IconButton>
        </div>
        <div className="max-h-[calc(94dvh-9rem)] overflow-y-auto px-5 py-5 sm:px-6">{children}</div>
        {footer && <div className="flex flex-col-reverse gap-2 border-t border-slate-100 bg-slate-50/70 px-5 py-4 sm:flex-row sm:justify-end sm:px-6">{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  tone = 'warning',
  loading,
}: {
  open: boolean
  onClose: () => void
  onConfirm: () => void | Promise<void>
  title: string
  description: string
  confirmLabel?: string
  cancelLabel?: string
  tone?: 'warning' | 'danger'
  loading?: boolean
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={<><Button variant="outline" onClick={onClose}>{cancelLabel}</Button><Button variant={tone === 'danger' ? 'danger' : 'primary'} loading={loading} onClick={() => void onConfirm()}>{confirmLabel}</Button></>}
    >
      <div className="flex gap-4">
        <div className={cn('grid h-11 w-11 shrink-0 place-items-center rounded-full', tone === 'danger' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-700')}><AlertTriangle className="h-5 w-5" /></div>
        <p className="pt-1 text-sm leading-6 text-slate-600">{description}</p>
      </div>
    </Modal>
  )
}

export function SavedIndicator({ state }: { state: 'idle' | 'saving' | 'saved' | 'error' }) {
  if (state === 'idle') return null
  if (state === 'saving') return <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500"><LoaderCircle className="h-3.5 w-3.5 animate-spin" />Guardando…</span>
  if (state === 'error') return <span className="text-xs font-semibold text-red-600">No se pudo guardar</span>
  return <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700"><Check className="h-3.5 w-3.5" />Guardado</span>
}

export { toast }
